import { Component, Inject } from '@angular/core';
import { MatDialogRef, MAT_DIALOG_DATA } from '@angular/material/dialog';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatInputModule } from '@angular/material/input';
import { MatDatepickerModule } from '@angular/material/datepicker';
import {
  DateAdapter,
  MAT_DATE_FORMATS,
  MAT_NATIVE_DATE_FORMATS,
} from '@angular/material/core';
import { MatButtonModule } from '@angular/material/button';
import { MatIconModule } from '@angular/material/icon';
import { MatDialogActions, MatDialogContent, MatDialogTitle } from '@angular/material/dialog';
import { ReportsBaseService } from '../services/reports-base.service';
import { DdMmYyyyNativeDateAdapter } from './adapters/dd-mm-yyyy-native-date-adapter';

@Component({
  selector: 'app-date-range-dialog',
  standalone: true,
  imports: [
    CommonModule,
    FormsModule,
    MatFormFieldModule,
    MatInputModule,
    MatDatepickerModule,
    MatButtonModule,
    MatIconModule,
    MatDialogActions,
    MatDialogContent,
    MatDialogTitle
  ],
  providers: [
    { provide: DateAdapter, useClass: DdMmYyyyNativeDateAdapter },
    { provide: MAT_DATE_FORMATS, useValue: MAT_NATIVE_DATE_FORMATS },
  ],
  templateUrl: './date-range-dialog.component.html',
  styleUrls: ['./date-range-dialog.component.scss']
})
export class DateRangeDialogComponent {
  startDate: Date | null;
  endDate: Date | null;
  private readonly originalStartDate: Date | null;
  private readonly originalEndDate: Date | null;

  constructor(
    public dialogRef: MatDialogRef<DateRangeDialogComponent>,
    @Inject(MAT_DIALOG_DATA) public data: { startDate: Date | null, endDate: Date | null },
    public baseService: ReportsBaseService,
    private dateAdapter: DateAdapter<Date>
  ) {
    this.startDate = data.startDate ? new Date(data.startDate) : null;
    this.endDate = data.endDate ? new Date(data.endDate) : null;
    this.originalStartDate = data.startDate ? new Date(data.startDate) : null;
    this.originalEndDate = data.endDate ? new Date(data.endDate) : null;
    
    this.dateAdapter.setLocale('en-GB'); // For dd-MM-YYYY format
  }

  setToday(): void {
    const start = new Date();
    start.setHours(0, 0, 0, 0); // Start of today

    const end = new Date();
    end.setHours(23, 59, 59, 999); // End of today

    this.startDate = start;
    this.endDate = end;
  }

  setThisWeek(): void {
    const today = new Date();
    const dayOfWeek = today.getDay(); // 0 is Sunday
    
    const startOfWeek = new Date(today);
    startOfWeek.setDate(today.getDate() - dayOfWeek);
    startOfWeek.setHours(0, 0, 0, 0); // Start of the week
    
    const endOfWeek = new Date(startOfWeek);
    endOfWeek.setDate(startOfWeek.getDate() + 6);
    endOfWeek.setHours(23, 59, 59, 999); // End of the week
    
    this.startDate = startOfWeek;
    this.endDate = endOfWeek;
  }

  setThisMonth(): void {
    const today = new Date();
    
    const startOfMonth = new Date(today.getFullYear(), today.getMonth(), 1);
    startOfMonth.setHours(0, 0, 0, 0); // Start of the month
    
    const endOfMonth = new Date(today.getFullYear(), today.getMonth() + 1, 0);
    endOfMonth.setHours(23, 59, 59, 999); // End of the month

    this.startDate = startOfMonth;
    this.endDate = endOfMonth;
  }

  resetDates(): void {
    this.startDate = null;
    this.endDate = null;
  }

  onCancel(): void {
    this.dialogRef.close();
  }

  onApply(): void {
    // Before passing the data back, strictly enforce the time boundaries
    if (this.startDate) {
      this.startDate.setHours(0, 0, 0, 0);
    }
    
    if (this.endDate) {
      this.endDate.setHours(23, 59, 59, 999);
    }

    this.dialogRef.close({ startDate: this.startDate, endDate: this.endDate });
    this.baseService.clearData();
  }
}